import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import { useGuestRideStore, type GuestRideStatusFilter } from '../stores/guestRideStore';
// The COLOCATED documents, not `@gql/schema`'s copies: `refetchQueries({ include })` matches on
// document identity, and the ported Rides tab runs `queries/getGuestRides.query.generated`. The
// legacy aggregate file declares the same operations as different objects, so importing from
// there would silently refetch nothing (finding F11).
import { GetGuestRideStatsDocument } from '../queries/getGuestRideStats.query.generated';
import { GetGuestRidesDocument } from '../queries/getGuestRides.query.generated';

/**
 * Action-only companion to useGuestRides for components that trigger
 * refreshes or navigation but never render ride data (the dialogs).
 *
 * useGuestRides mounts live watchers of the full list + stats queries and
 * subscribes to the whole filter store, so every caller re-renders on every
 * guest-rides cache write — including the subscription-driven refetches that
 * fire for any live ride on the rooftop. A dialog that only needs `refetch`
 * must not pay that cost: with the page's subscription active, an open
 * Request Ride dialog re-renders mid-keystroke on every live-ride event
 * (the nightly typing-freeze, see PR #659 review).
 *
 * This hook exposes the same three action signatures with zero query
 * watchers and zero store-state subscriptions (zustand action refs are
 * stable), so calling components never re-render from data traffic.
 */
export function useGuestRideActions() {
  const client = useApolloClient();
  const updateFilter = useGuestRideStore((s) => s.updateFilter);
  const setHighlightedRideId = useGuestRideStore((s) => s.setHighlightedRideId);

  // Refetches the page's active list + stats watchers (same effect as the
  // `refetch` returned by useGuestRides, minus owning a watcher ourselves).
  const refetch = useCallback(
    () => client.refetchQueries({ include: [GetGuestRidesDocument, GetGuestRideStatsDocument] }),
    [client],
  );

  const setStatusFilter = useCallback(
    (filter: GuestRideStatusFilter) => updateFilter('statusFilter', filter),
    [updateFilter],
  );

  return { refetch, setStatusFilter, setHighlightedRideId };
}
